<!-- Public Header -->
 <app-header></app-header>

<!-- Hero Section -->
<section class="hero-section">
  <div class="hero-content">
    <h2>AI Search & Marketing Intelligence</h2>
    <p>Transform your brand strategy with comprehensive AI-driven insights, competitive analysis, and market intelligence. Discover how your brand performs across digital platforms and optimize your market presence.</p>
    <div class="hero-actions">
      <button mat-raised-button color="primary" routerLink="/register" class="cta-button">
        <mat-icon>rocket_launch</mat-icon>
        Get Started
      </button>
      <button mat-button routerLink="/dashboard" class="demo-button">
        <mat-icon>play_circle</mat-icon>
        View Demo
      </button>
    </div>
  </div>
</section>

<!-- Why Choose AI Optima Section -->
<section class="why-choose-section">
  <div class="section-content">
    <div class="content-left">
      <h3>Why Choose AI Optima</h3>
      <p>In today's competitive market, brands need AI-powered insights to understand their digital presence, track competitor performance, and make data-driven decisions that drive growth.</p>

      <div class="stats-grid">
        <div class="stat-item">
          <div class="stat-number">95%</div>
          <div class="stat-label">Accuracy in Brand Analysis</div>
        </div>
        <div class="stat-item">
          <div class="stat-number">70%</div>
          <div class="stat-label">Faster Market Insights</div>
        </div>
        <div class="stat-item">
          <div class="stat-number">4x</div>
          <div class="stat-label">Better Brand Performance</div>
        </div>
      </div>
    </div>

    <div class="content-right">
      <div class="feature-highlights">
        <div class="highlight-item">
          <mat-icon>verified</mat-icon>
          <div>
            <h4>AI-Powered Analytics</h4>
            <p>Get real-time insights into your brand's digital presence and performance</p>
          </div>
        </div>

        <div class="highlight-item">
          <mat-icon>trending_up</mat-icon>
          <div>
            <h4>Competitive Intelligence</h4>
                <p>Stay ahead of competitors with advanced AI market analysis</p>
          </div>
        </div>

        <div class="highlight-item">
          <mat-icon>analytics</mat-icon>
          <div>
            <h4>Data-Driven Decisions</h4>
            <p>Make informed brand strategy decisions with comprehensive market data</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Brand Solutions Grid -->
<section class="solutions-grid-section">
  <div class="solutions-grid">
    <div class="solution-card">
      <div class="solution-icon">
        <mat-icon>security</mat-icon>
      </div>
      <h4>Brand Safety Monitoring</h4>
      <p>Monitor your brand reputation and safety across all digital platforms to maintain brand integrity and trust.</p>
    </div>

    <div class="solution-card">
      <div class="solution-icon">
        <mat-icon>data_array</mat-icon>
      </div>
      <h4>Market Intelligence</h4>
      <p>Access comprehensive market data and competitor insights to inform your brand strategy and positioning.</p>
    </div>

    <div class="solution-card">
      <div class="solution-icon">
        <mat-icon>campaign</mat-icon>
      </div>
      <h4>Brand Performance Analytics</h4>
      <p>Track and analyze your brand's performance across multiple channels and touchpoints.</p>
    </div>

    <div class="solution-card">
      <div class="solution-icon">
        <mat-icon>trending_up</mat-icon>
      </div>
      <h4>Competitive Analysis</h4>
      <p>Compare your brand performance against competitors and identify market opportunities.</p>
    </div>

    <div class="solution-card">
      <div class="solution-icon">
        <mat-icon>insights</mat-icon>
      </div>
      <h4>Consumer Insights</h4>
      <p>Understand consumer sentiment and behavior patterns to optimize your brand messaging.</p>
    </div>

    <div class="solution-card">
      <div class="solution-icon">
        <mat-icon>link</mat-icon>
      </div>
      <h4>Brand Integrations</h4>
      <p>Seamlessly integrate with your existing marketing tools and analytics platforms.</p>
    </div>

    <div class="solution-card">
      <div class="solution-icon">
        <mat-icon>trending_flat</mat-icon>
      </div>
      <h4>Brand Sentiment Analysis</h4>
      <p>Monitor and analyze brand sentiment across social media, reviews, and online conversations to understand public perception.</p>
    </div>

    <div class="solution-card">
      <div class="solution-icon">
        <mat-icon>auto_graph</mat-icon>
      </div>
      <h4>Predictive Brand Analytics</h4>
      <p>Leverage AI to predict brand performance trends and identify potential opportunities before they emerge in the market.</p>
    </div>
  </div>
</section>

<!-- Client Testimonials Section -->
<section class="testimonials-section">
  <div class="testimonials-content">
    <div class="section-header">
      <h3>What Our Clients Say</h3>
      <p>Trusted by leading brands to transform their market presence</p>
    </div>

    <div class="testimonials-carousel">
      <!-- Navigation Arrows -->
      <button class="carousel-arrow prev-arrow" (click)="prev()" mat-icon-button>
        <mat-icon>chevron_left</mat-icon>
      </button>

      <!-- Testimonial Cards Container -->
      <div class="testimonials-container">
        @for(testimonial of testimonials; track testimonial.id; let i = $index) {
          <div class="testimonial-card" 
               [class.active]="i === currentIndex"
               [class.prev]="i === (currentIndex - 1 + testimonials.length) % testimonials.length"
               [class.next]="i === (currentIndex + 1) % testimonials.length">
              
              <!-- Star Rating -->
              <div class="star-rating">
                @for(star of getStarArray(testimonial.rating); track $index) {
                  <mat-icon class="star-filled">star</mat-icon>
                }
              </div>

              <!-- Quote -->
              <p class="testimonial-quote">"{{ testimonial.quote }}"</p>

              <!-- Client Info -->
              <div class="client-info">
                <div class="client-avatar">
                  <mat-icon>account_circle</mat-icon>
                </div>
                <div class="client-details">
                  <h4>{{ testimonial.name }}</h4>
                  <p>{{ testimonial.role }}</p>
                  <p class="company-name">{{ testimonial.company }}</p>
                </div>
              </div>

              <!-- Bottom Section -->
              <div class="card-footer">
                <!-- Verified Badge -->
                @if(testimonial.verified) {
                  <div class="verified-badge">
                    <mat-icon>verified</mat-icon>
                    <span>Verified</span>
                  </div>
                }
                
                <!-- Industry Tag -->
                <div class="industry-tag">
                  <mat-icon>business</mat-icon>
                  {{ testimonial.industry }}
                </div>
            </div>
          </div>
        }
      </div>

      <!-- Navigation Arrows -->
      <button class="carousel-arrow next-arrow" (click)="next()" mat-icon-button>
        <mat-icon>chevron_right</mat-icon>
      </button>
    </div>

    <!-- Navigation Dots -->
    <div class="carousel-dots">
      @for(testimonial of testimonials; track testimonial.id; let i = $index) {
        <button class="dot" 
                [class.active]="i === currentIndex"
                (click)="goTo(i)">
        </button>
      }
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="cta-section">
  <div class="cta-content">
    <h3>Ready to Transform Your Brand Strategy?</h3>
    <p>Join leading brands using AI to optimize their market presence and drive growth</p>
    <button mat-raised-button color="primary" routerLink="/register" class="cta-button-large">
      <mat-icon>rocket_launch</mat-icon>
      Get Started Today
    </button>
  </div>
</section>
